// converts raw response data to objects that are easier for the frontend to handle
import {
  CreativeFormatPlaybackTime,
  DynamicSchemaDefinition,
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionFixed,
  DynamicSchemaDefinitionSelectMultiple,
  DynamicSchemaDefinitionSelectOne,
  Maybe,
  Project,
  Scalars,
  SupportingCreativeFormatNetwork,
  SupportingCreativeFormatPlaybackTime,
  SupportingCreativeFormatPlaybackTimeItem,
} from '../../../../generated/graphql';
import { AnyFieldValue, DynamicSchemaValue } from '../../../types/DynamicSchema';
import { InputtingCreativeFormat } from '../../../types/InputtingCreativeFormat';

const getInitialSelectedInputtingPlaybackTime = (
  playbackTime?: SupportingCreativeFormatPlaybackTime | null,
): Omit<CreativeFormatPlaybackTime, 'id'>[] => {
  let initialSelectedPlaybackTime: Omit<CreativeFormatPlaybackTime, 'id'> | null = null;

  if (playbackTime) {
    if (playbackTime.availables.find((playbackTimeItem) => playbackTimeItem.seconds === 15)) {
      initialSelectedPlaybackTime = {
        supportingPlaybackTimeId: playbackTime.id,
        requiredOption: playbackTime.availables.find((pb_availables) => pb_availables.seconds === 15)!.requiredOption,
        seconds: 15,
      };
    } else if (playbackTime.availables[0]) {
      initialSelectedPlaybackTime = {
        supportingPlaybackTimeId: playbackTime.id,
        requiredOption: (playbackTime.availables[0]! as SupportingCreativeFormatPlaybackTimeItem).requiredOption,
        seconds: (playbackTime.availables[0]! as SupportingCreativeFormatPlaybackTimeItem).seconds,
      };
    }
  }

  return initialSelectedPlaybackTime ? [initialSelectedPlaybackTime] : [];
};

export const convertSupportingCreativeFormatNetworksToInputtingCreativeFormats = (
  supportingCreativeFormatNetworks: ReadonlyArray<SupportingCreativeFormatNetwork>,
) => {
  const inputtingCreativeFormat: Array<InputtingCreativeFormat> = [];

  supportingCreativeFormatNetworks.forEach((network) => {
    network.placements.forEach((placement) => {
      placement.creativeTypes.forEach((creativeType) => {
        const initialSelectedPlaybackTime = getInitialSelectedInputtingPlaybackTime(
          creativeType.attribute.playbackTime,
        );
        if (creativeType.attribute.aspectRatio && creativeType.attribute.aspectRatio.availables.length > 0) {
          creativeType.attribute.aspectRatio.availables.forEach((aspectRatioItem) => {
            if (creativeType.attribute.aspectRatio != null) {
              inputtingCreativeFormat.push(({
                network,
                placement,
                creativeType,
                aspectRatioId: creativeType.attribute.aspectRatio!.id,
                requiredOption: creativeType.attribute.aspectRatio!,
                aspectRatioItem,
                playbackTime: creativeType.attribute.playbackTime,
                selected: false,
                show: true,
                selectedPlaybackTimes: initialSelectedPlaybackTime,
              } as unknown) as InputtingCreativeFormat);
            }
          });
        } else {
          inputtingCreativeFormat.push({
            network,
            placement,
            creativeType,
            aspectRatioId: '',
            aspectRatioItem: null,
            playbackTime: creativeType.attribute.playbackTime,
            selected: false,
            show: true,
            selectedPlaybackTimes: initialSelectedPlaybackTime,
          });
        }
      });
    });
  });

  return inputtingCreativeFormat;
};

export const convertProjectDynamicSchemaValues = ({
  values,
  definition,
}: {
  values?: Scalars['KeyValueObject'];
  definition?: Maybe<DynamicSchemaDefinition>;
}): DynamicSchemaValue | undefined => {
  if (values == null || definition == null) return;

  const valuesBuilder = {} as Record<string, AnyFieldValue>;
  const fields = definition.sections.flatMap((section) => section.fields);
  fields.forEach((field) => {
    const value =
      field.type === 'SELECT_MULTIPLE' ? (values[field.key] ? [...values[field.key]] : []) : values[field.key];
    valuesBuilder[field.key] = adaptFieldValue(field, value);
  });

  return {
    values: valuesBuilder,
    definition: definition,
  };
};

// Set the appropriate default values or data transformations for each DynamicSchema type
const adaptFieldValue = (field: DynamicSchemaDefinitionField, value: any) => {
  switch (field.type) {
    case 'SELECT_ONE': {
      const typedField = field as DynamicSchemaDefinitionSelectOne;
      const defaultValue = typedField.choices[0];
      return typedField.choices.find((choice) => choice.key === value)?.key ?? defaultValue.key;
    }
    case 'SELECT_MULTIPLE': {
      // DynamicSchemaのvalueが`Scalars['KeyValueObject'] === any`となっているため、どうしてもanyを使わないといけない
      return value.filter((item: any) =>
        (field as DynamicSchemaDefinitionSelectMultiple).choices.find((choice) => choice.key === item),
      );
    }
    case 'TOGGLE':
      return value || false;
    case 'SINGLE_LINE_TEXT':
      return typeof value === 'string' && !value.includes('\n') ? value : '';
    case 'MULTI_LINE_TEXT':
      return typeof value === 'string' ? value : '';
    case 'POSITIVE_INTEGER':
      return Number.isSafeInteger(value) && value >= 0 ? value : null;
    case 'FILES':
      return typeof value === 'string' ? value : null;
    case 'FIXED':
      return (field as DynamicSchemaDefinitionFixed).value;
    case 'FILE_IDS':
      return value || [];
    default:
      throw new Error(
        `DynamicSchema field key does not match those defined in DynamicSchemaDefinitionFieldType. (field.type: ${field.type}, value: ${value})`,
      );
  }
};

// APIから貰ったリスポンスオブジェクトのそれぞれのプロパティをフロントエンドで消化出来る形に変換するためのfunction.
// 本来であれば、APIから貰うオブジェクトが既にフロントエンドに消化しやすい形で返すのがベストだが、既存実装の都合上、この変換をフロントエンドで
// 行っている。
export const projectPropertyConverter = (project: Project) => ({
  creativeBriefValues: () =>
    convertProjectDynamicSchemaValues({
      values: project.creativeBriefValues,
      definition: project.creativeBriefDefinition,
    }),
  assetCategoryValues: () =>
    convertProjectDynamicSchemaValues({
      values: project.assetCategoryValues,
      definition: project.assetCategoryDefinition,
    }),
  inputtingCreativeFormats: () =>
    convertSupportingCreativeFormatNetworksToInputtingCreativeFormats(
      project.projectType.supportingCreativeFormatNetworks,
    ),
});
